import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Layout } from '../components/Layout';
import { FadeInUp, StaggerContainer, StaggerItem } from '../components/AnimatedSection';
import { ArrowRight, TrendingUp, Target, BarChart3 } from 'lucide-react';
import { Button } from '../components/ui/button';

const Home = () => {
  const { t } = useLanguage();

  return (
    <Layout>
      {/* Hero Section */}
      <section className="min-h-[90vh] flex items-center relative overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1739169294333-e73c270e90e8?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1920"
            alt="Industrial Technology"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 to-white/70" />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 relative z-10">
          <div className="max-w-3xl">
            <FadeInUp>
              <span className="confidential-badge mb-6 inline-block" data-testid="home-badge">
                {t('home.badge')}
              </span>
            </FadeInUp>

            <FadeInUp delay={0.1}>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-navy leading-tight mb-6" data-testid="home-title">
                {t('home.title')}
              </h1>
            </FadeInUp>

            <FadeInUp delay={0.2}>
              <p className="text-lg md:text-xl text-[#0066CC] font-medium mb-6" data-testid="home-subtitle">
                {t('home.subtitle')}
              </p>
            </FadeInUp>

            <FadeInUp delay={0.3}>
              <blockquote className="text-2xl md:text-3xl font-light text-slate-700 border-l-4 border-[#0066CC] pl-6 mb-8" data-testid="home-intro">
                "{t('home.intro')}"
              </blockquote>
            </FadeInUp>

            <FadeInUp delay={0.4}>
              <p className="text-base md:text-lg text-slate-600 leading-relaxed mb-10" data-testid="home-description">
                {t('home.description')}
              </p>
            </FadeInUp>

            <FadeInUp delay={0.5}>
              <Link to="/context">
                <Button 
                  className="bg-navy hover:bg-navy/90 text-white rounded-none px-8 py-6 text-sm uppercase tracking-widest font-semibold group"
                  data-testid="home-cta"
                >
                  {t('home.cta')}
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </FadeInUp>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <StaggerContainer className="grid md:grid-cols-3 gap-8" staggerDelay={0.15}>
            <StaggerItem>
              <div className="bg-white border border-slate-200 p-8 hover:border-[#0066CC] transition-colors" data-testid="stat-market">
                <BarChart3 className="w-8 h-8 text-[#0066CC] mb-4" />
                <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">
                  {t('home.stats.market')}
                </p>
                <p className="text-4xl font-oswald font-bold text-navy">
                  {t('home.stats.marketValue')}
                </p>
              </div>
            </StaggerItem>

            <StaggerItem>
              <div className="bg-white border border-slate-200 p-8 hover:border-[#0066CC] transition-colors" data-testid="stat-growth">
                <TrendingUp className="w-8 h-8 text-[#0066CC] mb-4" />
                <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">
                  {t('home.stats.growth')}
                </p>
                <p className="text-4xl font-oswald font-bold text-navy">
                  {t('home.stats.growthValue')}
                </p>
              </div>
            </StaggerItem>

            <StaggerItem>
              <div className="bg-white border border-slate-200 p-8 hover:border-[#0066CC] transition-colors" data-testid="stat-projection">
                <Target className="w-8 h-8 text-[#0066CC] mb-4" />
                <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">
                  {t('home.stats.projection')}
                </p>
                <p className="text-4xl font-oswald font-bold text-navy">
                  {t('home.stats.projectionValue')}
                </p>
              </div>
            </StaggerItem>
          </StaggerContainer>
        </div>
      </section>

      {/* Quick Navigation - All Pages */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeInUp>
            {/* Mobile: Horizontal scroll | Desktop: Grid */}
            <div 
              className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory md:grid md:grid-cols-3 lg:grid-cols-4 md:overflow-visible md:pb-0"
              style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch'
              }}
            >
              {[
                { to: '/context', icon: '01', label: 'nav.context' },
                { to: '/construction', icon: '02', label: 'nav.construction' },
                { to: '/industries', icon: '03', label: 'nav.industries' },
                { to: '/legislation', icon: '04', label: 'nav.legislation' },
                { to: '/microfactory', icon: '05', label: 'nav.microfactory' },
                { to: '/evolution', icon: '06', label: 'nav.evolution' },
                { to: '/sources', icon: '07', label: 'nav.sources' }
              ].map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="group flex-shrink-0 w-[calc(50%-8px)] md:w-auto md:flex-shrink p-6 border border-slate-200 hover:border-[#0066CC] hover:bg-slate-50 transition-all snap-start"
                  data-testid={`quick-nav-${item.to.slice(1)}`}
                >
                  <span className="text-3xl font-oswald font-bold text-slate-200 group-hover:text-[#0066CC] transition-colors">
                    {item.icon}
                  </span>
                  <p className="mt-2 text-sm font-semibold text-slate-700 group-hover:text-navy transition-colors">
                    {t(item.label)}
                  </p>
                  <ArrowRight className="w-4 h-4 mt-3 text-slate-400 group-hover:text-[#0066CC] group-hover:translate-x-1 transition-all" />
                </Link>
              ))}
            </div>
          </FadeInUp>
        </div>
      </section>
    </Layout>
  );
};

export default Home;
